"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Menu, X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Header — UPDATED:
 * - Logo is larger (almost the height of the header).
 * - Logo navigates to "/" when clicked.
 * - "Home" option added to navigation.
 * - Slightly more gold across header effects (sweep, halo, bar).
 * - Preserves prior UX (reliable actions, mobile menu, flame CTA).
 *
 * Paste/replace your current Header component file with this.
 */

/* ---------------------- Theme constants ---------------------- */
const GOLD_HEX = "#FFD700";
const GOLD_RGBA = "255,215,0";
const BG = "#030305";
const FG = "#FFFFFF";

/* ---------------- reliable action hook ---------------- */
function useReliableAction<T extends (...args: any[]) => void>(handler: T, options?: { lockMs?: number }) {
  const lockMs = options?.lockMs ?? 420;
  const runningRef = useRef(false);

  const run = useCallback(
    (...args: any[]) => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        handler(...(args as any));
      } catch {
        // swallow
      } finally {
        window.setTimeout(() => {
          runningRef.current = false;
        }, lockMs);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handler, lockMs]
  );

  const props = useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => {
        if ((e as any).button && (e as any).button !== 0) return;
        e.stopPropagation?.();
        run(e);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          run(e);
        }
      },
      role: "button",
      tabIndex: 0,
      "data-reliable": "1",
    }),
    [run]
  );

  return { run, props };
}

/* ---------------------- Flame mini-component ---------------------- */
function Flame({ size = 18, intensity = 1 }: { size?: number; intensity?: number }) {
  const uid = `flame-${Math.round(Math.random() * 1e6)}`;
  const w = Math.max(14, size);
  const h = Math.max(20, Math.round(size * 1.4));
  const styleString = `
    @keyframes ${uid}-f { 0% { transform: translateY(0); opacity: 0.92 } 40% { transform: translateY(-6%); opacity: 1 } 80% { transform: translateY(0); opacity: 0.86 } 100% { transform: translateY(0); opacity: 0.92 } }
    .${uid} { display:inline-block; position:relative; width:${w}px; height:${h}px; pointer-events:none; transform-origin:center bottom; }
    .${uid} .layer { position:absolute; left:50%; transform:translateX(-50%); bottom:0; border-radius:50% 40% 20% 40% / 70% 60% 50% 40%; will-change:transform,opacity,filter; filter:saturate(120%); }
    .${uid} .l1 { width:${Math.round(w*0.82)}px; height:${Math.round(h*0.92)}px; background: radial-gradient(circle at 40% 25%, rgba(${GOLD_RGBA},0.98), rgba(255,140,20,0.06) 45%, transparent 78%); opacity:${0.94 * intensity}; animation:${uid}-f ${700 + Math.round(Math.random()*300)}ms infinite ease-in-out; }
    .${uid} .l2 { width:${Math.round(w*0.56)}px; height:${Math.round(h*0.66)}px; background: radial-gradient(circle at 50% 18%, rgba(255,220,120,0.98), rgba(255,120,10,0.04) 45%, transparent 68%); opacity:${0.86 * intensity}; animation:${uid}-f ${800 + Math.round(Math.random()*320)}ms infinite ease-in-out; }
    .${uid} .l3 { width:${Math.round(w*0.36)}px; height:${Math.round(h*0.44)}px; background: radial-gradient(circle at 50% 10%, rgba(255,255,255,0.96), rgba(255,220,70,0.06) 40%, transparent 68%); opacity:${0.78 * intensity}; animation:${uid}-f ${650 + Math.round(Math.random()*220)}ms infinite ease-in-out; }
  `;
  return (
    <span aria-hidden className={uid} style={{ display: "inline-block", verticalAlign: "middle" }}>
      <style>{styleString}</style>
      <span className="layer l1" />
      <span className="layer l2" />
      <span className="layer l3" />
    </span>
  );
}

/* ---------------------- Nav data (Home added) ---------------------- */
const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Stories", href: "/stories" },
  { label: "New Story", href: "/dashboard/new-story" },
  { label: "How to Use", href: "/how-to" },
  { label: "Pricing", href: "/subscribe" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
] as const;

const AUTH_LINKS = [
  { label: "Sign In", href: "/login" },
  { label: "Sign Up", href: "/signup" },
] as const;

const DASHBOARD_LINK = { label: "Dashboard", href: "/dashboard" };

/* ---------------------- Header component ---------------------- */
export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const supabase = createClient();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!mounted) return;
        setIsAuthenticated(!!session?.user);
      } catch {
        if (!mounted) return;
        setIsAuthenticated(false);
      } finally {
        if (mounted) setCheckingAuth(false);
      }
    };
    check();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const toggleMenu = useReliableAction(() => setIsMobileMenuOpen((s) => !s), { lockMs: 240 });
  const goTo = useReliableAction((to: string) => {
    setIsMobileMenuOpen(false);
    router.push(to);
  }, { lockMs: 420 });

  const buildLink = (link: { label: string; href: string }) => {
    const isActive = pathname === link.href;
    return (
      <Link key={link.href} href={link.href} prefetch={false} legacyBehavior>
        <a className={cn("nav-link", isActive ? "active" : "")} onClick={() => setIsMobileMenuOpen(false)}>
          {link.label}
        </a>
      </Link>
    );
  };

  return (
    <header className="sc-header" role="banner">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700;800&family=Inter:wght@300;400;600;700;800&display=swap');

        :root {
          --gold: ${GOLD_HEX};
          --gold-rgb: ${GOLD_RGBA};
          --bg: ${BG};
          --fg: ${FG};
        }

        /* header baseline */
        .sc-header {
          position: sticky;
          top: 0;
          z-index: 1200;
          width: 100%;
          background: linear-gradient(180deg, rgba(3,3,5,0.80), rgba(0,0,0,0.72));
          color: var(--fg);
          box-shadow: 0 10px 40px rgba(0,0,0,0.6);
          border-bottom: 1px solid rgba(255,255,255,0.02);
          backdrop-filter: blur(10px) saturate(120%);
        }

        /* visual effect layer (more gold intensity) */
        .header-effect {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
          mix-blend-mode: screen;
        }
        .gold-sweep {
          position: absolute;
          left: -50%;
          top: -30%;
          width: 200%;
          height: 200%;
          background:
            radial-gradient(40% 40% at 22% 30%, rgba(var(--gold-rgb),0.12), transparent 9%),
            linear-gradient(120deg, rgba(var(--gold-rgb),0.06), rgba(var(--gold-rgb),0.04) 30%, rgba(0,0,0,0));
          transform: rotate(-14deg);
          filter: blur(34px) saturate(150%);
          animation: sweep 8s linear infinite;
          opacity: 1;
        }
        @keyframes sweep {
          0% { transform: translateX(-36%) rotate(-14deg) scale(1); opacity: 0.92; }
          50% { transform: translateX(6%) rotate(-14deg) scale(1.02); opacity: 1; }
          100% { transform: translateX(48%) rotate(-14deg) scale(1); opacity: 0.92; }
        }

        .micro-dots {
          position:absolute;
          inset:0;
          background-image:
            radial-gradient(rgba(var(--gold-rgb),0.08) 1px, transparent 1px),
            radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 20px 20px, 44px 44px;
          opacity: 0.9;
          filter: blur(5px);
          animation: drift 11s linear infinite;
        }
        @keyframes drift {
          0% { transform: translateY(0); opacity: 0.9; }
          50% { transform: translateY(-5px); opacity: 1; }
          100% { transform: translateY(0); opacity: 0.9; }
        }

        /* inner container */
        .sc-inner {
          position: relative;
          z-index: 3;
          max-width: 1340px;
          margin: 0 auto;
          padding: clamp(10px, 2.2vw, 18px) clamp(12px, 3vw, 28px);
          display: flex;
          align-items: center;
          gap: 18px;
          min-height: 104px; /* header taller to accommodate larger logo */
          box-sizing: border-box;
        }

        /* logo area - make logo almost as tall as header */
        .logo-wrap { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .logo-container {
          position: relative;
          width: 92px; /* large logo */
          height: 92px;
          display: inline-grid;
          place-items: center;
          pointer-events: auto;
        }

        /* frame with stronger golden glow */
        .logo-frame {
          width: 92px;
          height: 92px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          position: relative;
          box-sizing: border-box;
          z-index: 4;
          border: 1.5px solid rgba(var(--gold-rgb),0.24);
          background: rgba(0,0,0,0.16);
          backdrop-filter: blur(6px) saturate(120%);
          box-shadow: 0 12px 56px rgba(var(--gold-rgb),0.14), inset 0 2px 10px rgba(255,255,255,0.02);
          transition: transform 280ms cubic-bezier(.2,.9,.25,1), box-shadow 280ms;
        }
        .logo-frame:hover { transform: translateY(-4px) scale(1.01); box-shadow: 0 18px 84px rgba(var(--gold-rgb),0.20), inset 0 2px 12px rgba(255,255,255,0.03); }

        .logo-frame::after {
          content: "";
          position: absolute;
          inset: -12px;
          border-radius: 20px;
          filter: blur(18px) saturate(140%);
          background: radial-gradient(circle at 40% 35%, rgba(var(--gold-rgb),0.72), rgba(var(--gold-rgb),0.25) 22%, transparent 46%);
          opacity: 0.98;
          transform: scale(0.99);
          pointer-events:none;
        }

        .logo-halo {
          position: absolute;
          width: 116px;
          height: 116px;
          border-radius: 18px;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%) rotate(0deg);
          background: conic-gradient(from 0deg, rgba(var(--gold-rgb),0.12), rgba(var(--gold-rgb),0.05), rgba(255,255,255,0.01));
          filter: blur(20px) saturate(150%);
          opacity: 0.95;
          z-index: 2;
          pointer-events: none;
          animation: halo-rotate 12s linear infinite;
        }
        @keyframes halo-rotate {
          0% { transform: translate(-50%,-50%) rotate(0deg) scale(1); opacity: 0.9; }
          50% { transform: translate(-50%,-50%) rotate(180deg) scale(1.03); opacity: 1; }
          100% { transform: translate(-50%,-50%) rotate(360deg) scale(1); opacity: 0.9; }
        }

        /* nav */
        .nav { display:flex; gap:8px; align-items:center; flex:1; justify-content:flex-start; min-width:0; }
        .nav-list { display:none; gap:8px; align-items:center; }
        .nav-link {
          display:inline-block;
          padding:8px 12px;
          border-radius:10px;
          font-weight:650;
          color: rgba(255,255,255,0.94);
          text-decoration:none;
          transition: all 220ms cubic-bezier(.2,.9,.25,1);
          font-family: 'Inter','Outfit', system-ui;
        }
        .nav-link:hover { color: var(--gold); transform: translateY(-2px); }
        .nav-link.active { color: var(--gold); background: linear-gradient(180deg, rgba(var(--gold-rgb),0.08), rgba(255,255,255,0.02)); box-shadow: 0 10px 46px rgba(var(--gold-rgb),0.10); }

        /* actions */
        .actions { display:flex; gap:10px; align-items:center; }

        .btn { appearance:none; border:none; border-radius:10px; padding:8px 12px; display:inline-flex; align-items:center; gap:8px; cursor:pointer; font-weight:800; font-family: 'Inter','Outfit'; transition: transform 220ms cubic-bezier(.2,.9,.25,1), box-shadow 220ms; background: linear-gradient(90deg, rgba(var(--gold-rgb),0.98), rgba(255,242,180,0.92)); color: #0b0b0d; box-shadow: 0 12px 40px rgba(var(--gold-rgb), 0.18), 0 2px 10px rgba(0,0,0,0.6); }
        .btn-burn { position:relative; background: linear-gradient(90deg, rgba(255,245,160,1), rgba(255,235,120,0.98)); box-shadow: 0 20px 72px rgba(var(--gold-rgb),0.24); }
        .btn-burn::after { content:''; position:absolute; inset:auto -10px -18px -10px; height:44px; border-radius:16px; pointer-events:none; background: radial-gradient(ellipse at center, rgba(var(--gold-rgb),0.26), rgba(0,0,0,0)); filter: blur(14px); }
        .btn-ghost { background: transparent; color: rgba(255,255,255,0.94); border: 1px solid rgba(255,255,255,0.04); padding:8px 10px; }

        .mobile-toggle { display:inline-flex; align-items:center; justify-content:center; width:48px; height:48px; border-radius:12px; background: transparent; color: var(--gold); }

        /* gold decorative bar — slightly stronger */
        .gold-bar { height: 4px; width: 100%; background: linear-gradient(90deg, rgba(255,240,180,0.0), rgba(var(--gold-rgb),0.98), rgba(255,240,180,0.0)); filter: drop-shadow(0 10px 30px rgba(var(--gold-rgb),0.14)); position: relative; z-index: 4; }

        .mobile-panel { position:absolute; left:0; right:0; top:100%; z-index:1100; background: linear-gradient(180deg, rgba(var(--gold-rgb),0.06), rgba(0,0,0,0.66)); border-top: 1px solid rgba(var(--gold-rgb),0.14); box-shadow: 0 16px 100px rgba(0,0,0,0.7); padding: 12px; backdrop-filter: blur(12px) saturate(120%); }

        .sc-header, .sc-inner, .nav, .nav-list, .actions { min-width: 0; }

        @media (min-width: 992px) {
          .nav-list { display:flex; }
          .mobile-toggle { display:none; }
        }
        @media (max-width: 991px) {
          .sc-inner { min-height: 88px; padding: 10px 12px; gap: 10px; }
          .logo-container { width: 84px; height: 84px; }
          .logo-frame { width: 84px; height: 84px; border-radius: 12px; }
          .logo-halo { width: 104px; height: 104px; border-radius: 16px; }
        }
        @media (max-width: 640px) {
          .logo-container { width: 72px; height: 72px; }
          .logo-frame { width: 72px; height: 72px; border-radius: 12px; }
          .logo-halo { width: 92px; height: 92px; border-radius: 14px; }
        }
      `}</style>

      {/* header visuals */}
      <div className="header-effect" aria-hidden>
        <div className="gold-sweep" />
        <div className="micro-dots" />
      </div>

      <div className="sc-inner">
        {/* Logo - now larger and links to / */}
        <div className="logo-wrap">
          <Link href="/" legacyBehavior>
            <a
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Go home"
              title="Home"
              style={{ display: "inline-block", textDecoration: "none" }}
            >
              <div className="logo-container" aria-hidden>
                <div className="logo-halo" />
                <div className="logo-frame" aria-hidden>
                  <div style={{ position: "relative", width: "72%", height: "72%" }}>
                    <Image
                      src="/logo.png"
                      alt="Logo"
                      fill
                      sizes="72px"
                      style={{ objectFit: "contain", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.6))" }}
                      onError={(e) => {
                        const el = e.currentTarget as HTMLImageElement;
                        if (el) el.style.display = "none";
                      }}
                    />
                  </div>
                </div>
              </div>
            </a>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="nav" aria-label="Primary navigation">
          <div className="nav-list" role="menubar">
            {NAV_LINKS.map((l) => {
              const isActive = pathname === l.href;
              return (
                <Link key={l.href} href={l.href} prefetch={false} legacyBehavior>
                  <a className={cn("nav-link", isActive ? "active" : "")} onClick={() => setIsMobileMenuOpen(false)}>
                    {l.label}
                  </a>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Actions (auth/ctas + mobile toggle) */}
        <div className="actions" role="region" aria-label="Header actions">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }} className="desktop-actions">
            {checkingAuth ? (
              <div style={{ width: 36, height: 36, display: "grid", placeItems: "center", color: "rgba(255,255,255,0.9)" }}>
                <Loader2 className="spin" />
              </div>
            ) : isAuthenticated ? (
              <button
                {...(goTo.props as any)}
                onPointerDown={() => goTo.run(DASHBOARD_LINK.href)}
                className="btn btn-ghost"
                aria-label="Open Dashboard"
                title="Dashboard"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  {...(goTo.props as any)}
                  onPointerDown={() => goTo.run(AUTH_LINKS[0].href)}
                  className="btn btn-ghost"
                  aria-label="Sign in"
                >
                  Sign In
                </button>

                <button
                  {...(goTo.props as any)}
                  onPointerDown={() => goTo.run(AUTH_LINKS[1].href)}
                  className="btn btn-burn"
                  aria-label="Sign up"
                  title="Sign up"
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    Sign Up
                    <span style={{ transform: "translateY(-2px)" }}>
                      <Flame size={16} intensity={1} />
                    </span>
                  </span>
                </button>
              </>
            )}
          </div>

          <div style={{ display: "inline-flex" }}>
            <button
              {...(toggleMenu.props as any)}
              onPointerDown={(e) => {
                // @ts-ignore
                toggleMenu.run(e);
              }}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              className="mobile-toggle"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      <div className="gold-bar" />

      {/* Mobile dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-panel"
            initial={{ opacity: 0, y: -8, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.995 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="mobile-panel"
            role="dialog"
            aria-label="Mobile navigation"
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {NAV_LINKS.map((link) => (
                <Link key={link.href} href={link.href} legacyBehavior>
                  <a
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn("nav-link", pathname === link.href ? "active" : "")}
                    style={{ padding: "12px 14px", borderRadius: 10 }}
                  >
                    {link.label}
                  </a>
                </Link>
              ))}

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {checkingAuth ? (
                  <div style={{ display: "grid", placeItems: "center", width: 36, height: 36 }}>
                    <Loader2 className="spin" />
                  </div>
                ) : isAuthenticated ? (
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      router.push(DASHBOARD_LINK.href);
                    }}
                    className="btn"
                    style={{ flex: 1 }}
                  >
                    Dashboard
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        router.push(AUTH_LINKS[0].href);
                      }}
                      className="btn btn-ghost"
                      style={{ flex: 1 }}
                    >
                      Sign In
                    </button>

                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        router.push(AUTH_LINKS[1].href);
                      }}
                      className="btn btn-burn"
                      style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                    >
                      Sign Up <span style={{ marginLeft: 8 }}><Flame size={14} intensity={0.95} /></span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
